import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { campaignApi } from '@/api/feedback/campaignApi';
import { type Location } from '@/models/account-management/Location';
import { CampaignScheduleScope, type Campaign, type CampaignSchedule, type Question } from '@/models/feedback/Campaign';
import { useLocalizedStrings } from '../../../localization/LocalizedStringsProvider';
import { useToast } from '../../common/toast/ToastProvider';
import ReadOnlyFieldset from '../../common/dialog/ReadOnlyFieldset';
import ScheduleDialog from './ScheduleDialog';
import { useAssignedLocations } from '../../../permissions/useLocationScope';

// Must match MaxQuestionsPerCampaign in TouchTab.Feedback, which rejects a campaign carrying more. This
// is the friendly half of that rule - stopping the operator at the limit rather than letting them build a
// campaign the save then refuses.
//
// The limit is not about rendering. Each question is another line in the alert SMS, billed by segment, and
// another prompt before the guest is done - so it moves when somebody decides that trade has changed, and
// both halves have to move together.
const MAX_QUESTIONS = 10;

interface Props {
    open: boolean;
    brandId: string;
    campaign: Campaign | null; // null = create
    questions: Question[];
    // View-only: the grid opens this with an eye rather than a pencil for read access alone.
    readOnly?: boolean;
    onClose: () => void;
}

const CampaignDialog = ({ open, brandId, campaign, questions, readOnly, onClose }: Props) => {
    const qc = useQueryClient();
    const toast = useToast();
    const strings = useLocalizedStrings();

    const scopeLabel = (scope: CampaignScheduleScope) => {
        switch (scope) {
            case CampaignScheduleScope.Brand: return strings.feedbackScopeBrand;
            case CampaignScheduleScope.Location: return strings.feedbackScopeLocation;
            case CampaignScheduleScope.LocationGroup: return strings.feedbackScopeLocationGroup;
            default: return '';
        }
    };

    const [name, setName] = useState('');
    const [instruction, setInstruction] = useState('');
    const [selectedQuestionIds, setSelectedQuestionIds] = useState<string[]>([]);

    const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
    const [editingSchedule, setEditingSchedule] = useState<CampaignSchedule | null>(null);

    useEffect(() => {
        if (!open) return;
        setName(campaign?.name ?? '');
        setInstruction(campaign?.instruction ?? '');
        setSelectedQuestionIds(campaign?.questionIds ?? []);
    }, [open, campaign]);

    // Only the locations this user is assigned to (TTD-160).
    const { locations } = useAssignedLocations(brandId, { enabled: open });

    const { data: schedules } = useQuery<CampaignSchedule[]>({
        queryKey: ['feedback-schedules', campaign?.id ?? ''],
        enabled: open && !!campaign,
        queryFn: () => campaignApi.listSchedules(campaign!.id),
        staleTime: 0,
    });

    const { mutateAsync: save, isPending } = useMutation({
        mutationFn: async () => {
            if (campaign) {
                await campaignApi.updateCampaign(campaign.id, { name, instruction, questionIds: selectedQuestionIds });
            } else {
                await campaignApi.createCampaign({ brandId, name, instruction, questionIds: selectedQuestionIds });
            }
        },
        onSuccess: async () => {
            toast({ severity: 'success', message: strings.feedbackCampaignSaved });
            await qc.invalidateQueries({ queryKey: ['feedback-campaigns', brandId] });
            onClose();
        },
        onError: () => toast({ severity: 'error', message: strings.feedbackCampaignSaveError }),
    });

    const { mutateAsync: deleteSchedule } = useMutation({
        mutationFn: (id: string) => campaignApi.deleteSchedule(id),
        onSuccess: async () => {
            toast({ severity: 'success', message: strings.feedbackScheduleDeleted });
            await qc.invalidateQueries({ queryKey: ['feedback-schedules', campaign?.id ?? ''] });
        },
        onError: () => toast({ severity: 'error', message: strings.feedbackScheduleDeleteError }),
    });

    const addQuestion = (id: string) =>
        setSelectedQuestionIds((prev) => (prev.length >= MAX_QUESTIONS || prev.includes(id) ? prev : [...prev, id]));
    const removeQuestion = (id: string) =>
        setSelectedQuestionIds((prev) => prev.filter((x) => x !== id));
    const moveQuestion = (index: number, dir: -1 | 1) =>
        setSelectedQuestionIds((prev) => {
            const target = index + dir;
            if (target < 0 || target >= prev.length) return prev;
            const next = [...prev];
            [next[index], next[target]] = [next[target], next[index]];
            return next;
        });

    const canSave = name.trim().length > 0 && instruction.trim().length > 0;

    const openNewSchedule = () => { setEditingSchedule(null); setScheduleDialogOpen(true); };
    const openEditSchedule = (s: CampaignSchedule) => { setEditingSchedule(s); setScheduleDialogOpen(true); };

    return (
        <>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
                <DialogTitle>{campaign ? strings.feedbackEditCampaign : strings.feedbackNewCampaign}</DialogTitle>
                <DialogContent>
                <ReadOnlyFieldset readOnly={readOnly}>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField label={strings.name} value={name} onChange={(e) => setName(e.target.value)} fullWidth autoFocus />
                        <TextField label={strings.feedbackInstruction} value={instruction} onChange={(e) => setInstruction(e.target.value)} fullWidth multiline minRows={2} />

                        <Box>
                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                                <Typography variant="subtitle1">{strings.feedbackQuestions}</Typography>
                                <Typography variant="caption" color="text.secondary">{selectedQuestionIds.length}/{MAX_QUESTIONS}</Typography>
                            </Stack>

                            {/* Ordered list — the order is the order the guest sees the questions. */}
                            <Stack spacing={1} sx={{ mt: 1 }}>
                                {selectedQuestionIds.map((id, index) => (
                                    <Box key={id} sx={{ display: 'flex', alignItems: 'center', border: 1, borderColor: 'divider', borderRadius: 1, pl: 1.5, pr: 0.5, py: 0.5 }}>
                                        <Typography variant="body2" sx={{ width: 22, color: 'text.secondary' }}>{index + 1}.</Typography>
                                        <Typography variant="body2" sx={{ flex: 1 }}>{questions.find((q) => q.id === id)?.name ?? ''}</Typography>
                                        <IconButton size="small" onClick={() => moveQuestion(index, -1)} disabled={index === 0} aria-label="move question up"><ArrowUpwardIcon fontSize="small" /></IconButton>
                                        <IconButton size="small" onClick={() => moveQuestion(index, 1)} disabled={index === selectedQuestionIds.length - 1} aria-label="move question down"><ArrowDownwardIcon fontSize="small" /></IconButton>
                                        <IconButton size="small" onClick={() => removeQuestion(id)} aria-label="remove question"><DeleteIcon fontSize="small" color="error" /></IconButton>
                                    </Box>
                                ))}
                                {selectedQuestionIds.length === 0 && (
                                    <Typography variant="body2" color="text.secondary">{strings.feedbackNoQuestionsSelected}</Typography>
                                )}
                            </Stack>

                            <FormControl fullWidth sx={{ mt: 1.5 }} disabled={selectedQuestionIds.length >= MAX_QUESTIONS}>
                                <InputLabel id="add-question-label">{strings.feedbackAddQuestion}</InputLabel>
                                <Select
                                    labelId="add-question-label"
                                    label={strings.feedbackAddQuestion}
                                    value=""
                                    onChange={(e) => addQuestion(e.target.value as string)}
                                >
                                    {questions.filter((q) => !selectedQuestionIds.includes(q.id)).map((q) => (
                                        <MenuItem key={q.id} value={q.id}>{q.name}</MenuItem>
                                    ))}
                                </Select>
                                {selectedQuestionIds.length >= MAX_QUESTIONS && (
                                    <FormHelperText>{strings.feedbackMaxQuestions}</FormHelperText>
                                )}
                            </FormControl>
                        </Box>

                        <Divider />

                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography variant="subtitle1">{strings.feedbackSchedules}</Typography>
                            <Button size="small" onClick={openNewSchedule} disabled={!campaign}>{strings.feedbackAddSchedule}</Button>
                        </Stack>

                        {!campaign && (
                            <Typography variant="body2" color="text.secondary">{strings.feedbackSaveCampaignFirst}</Typography>
                        )}

                        {campaign && (schedules?.length ?? 0) === 0 && (
                            <Typography variant="body2" color="text.secondary">{strings.feedbackNoSchedules}</Typography>
                        )}

                        {campaign && schedules?.map((s) => (
                            <Box key={s.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: 1, borderColor: 'divider', borderRadius: 1, px: 2, py: 1 }}>
                                <Box>
                                    <Typography variant="body2">{scopeLabel(s.scope)}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {new Date(s.startDateTime).toLocaleString()} — {new Date(s.endDateTime).toLocaleString()}
                                    </Typography>
                                </Box>
                                <Box>
                                    <IconButton size="small" onClick={() => openEditSchedule(s)} aria-label="edit schedule"><EditIcon fontSize="small" color="secondary" /></IconButton>
                                    <IconButton size="small" onClick={() => deleteSchedule(s.id)} aria-label="delete schedule"><DeleteIcon fontSize="small" color="error" /></IconButton>
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                </ReadOnlyFieldset>
            </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={onClose}>{readOnly ? strings.close : strings.cancel}</Button>
                    {!readOnly && <Button variant="contained" loading={isPending} disabled={!canSave || isPending} onClick={() => save()}>{strings.save}</Button>}
                </DialogActions>
            </Dialog>

            {campaign && (
                <ScheduleDialog
                    open={scheduleDialogOpen}
                    campaignId={campaign.id}
                    brandId={brandId}
                    locations={locations ?? []}
                    schedule={editingSchedule}
                    onClose={() => setScheduleDialogOpen(false)}
                />
            )}
        </>
    );
};

export default CampaignDialog;
